/**
 * Display legible de `ItemModifiers` para UI (pantalla de creación de personaje,
 * tooltips del panel de personaje, wiki). Convierte un mapa de modifiers en una
 * lista de filas `{ label, value, format }` con nombres en español.
 *
 * Fuente de nombres:
 *   - keys ÚNICAS (talkToDead, unboundSpellRange, ...) → `getUniqueModifier(key)`
 *     (mismo displayName que muestra el tooltip del item).
 *   - resto → `MODIFIER_LABELS`.
 *   - fallback → la propia key (para no romper si falta una etiqueta).
 */
import type { ItemModifiers } from '../types/item';
import { getUniqueModifier, isUniqueModifierKey } from './uniqueModifiers';
import { getKingdomLabel, type KingdomId } from './kingdoms';
import { getSkillDef } from './skills';

export type ModifierDisplayFormat = 'flat' | 'percent' | 'flag';

interface ModifierLabelDef {
  label: string;
  format: ModifierDisplayFormat;
}

/** Etiquetas + formato de los modifiers NO únicos. */
export const MODIFIER_LABELS: Partial<Record<keyof ItemModifiers, ModifierLabelDef>> = {
  hp: { label: 'Vida', format: 'flat' },
  mana: { label: 'Maná', format: 'flat' },
  stamina: { label: 'Energía', format: 'flat' },
  minDamage: { label: 'Golpe Mínimo', format: 'flat' },
  maxDamage: { label: 'Golpe Máximo', format: 'flat' },
  physDef: { label: 'Defensa Física', format: 'flat' },
  magicDef: { label: 'Defensa Mágica', format: 'flat' },
  moveSpeed: { label: 'Vel. Movimiento', format: 'flat' },
  attackSpeed: { label: 'Vel. Ataque', format: 'flat' },
  castSpeed: { label: 'Vel. Casteo', format: 'flat' },
  evasion: { label: 'Evasión', format: 'percent' },
  critChance: { label: 'Crítico', format: 'percent' },
  critMultiplier: { label: 'Mult. Crítico', format: 'percent' },
  regenHP: { label: 'Regen Vida', format: 'flat' },
  regenMana: { label: 'Regen Maná', format: 'flat' },
  regenStamina: { label: 'Regen Energía', format: 'flat' },
  hpPercent: { label: 'Vida %', format: 'percent' },
  manaPercent: { label: 'Maná %', format: 'percent' },
  staminaPercent: { label: 'Energía %', format: 'percent' },
  damagePercent: { label: 'Daño %', format: 'percent' },
  attackSpeedPercent: { label: 'Vel. Ataque %', format: 'percent' },
  moveSpeedPercent: { label: 'Vel. Movimiento %', format: 'percent' },
  critChancePercent: { label: 'Crítico %', format: 'percent' },
  critMultiplierPercent: { label: 'Mult. Crítico %', format: 'percent' },
  critDamageReductionPercent: { label: 'Reducción Daño Crítico', format: 'percent' },
  meleeDamagePercent: { label: 'Daño Cuerpo a cuerpo %', format: 'percent' },
  projectileDamagePercent: { label: 'Daño a Distancia %', format: 'percent' },
  spellDamagePercent: { label: 'Daño Mágico %', format: 'percent' },
  spellRangeBonus: { label: 'Alcance de Hechizos', format: 'flat' },
  lifeStealPhysical: { label: 'Robo Vida Físico', format: 'percent' },
  lifeStealMagic: { label: 'Robo Vida Mágico', format: 'percent' },
  reflectPhysicalDamage: { label: 'Reflejo Físico', format: 'percent' },
  reflectMagicDamage: { label: 'Reflejo Mágico', format: 'percent' },
  healingOutput: { label: 'Curación Otorgada', format: 'percent' },
  healingReceived: { label: 'Curación Recibida', format: 'percent' },
  magicFind: { label: 'Magic Find', format: 'percent' },
  // Mods de minería (picos skiller / nodo del Enano). Ver docs/MINING_AND_SMITHING.md.
  miningGemChanceBonus: { label: 'Chance de Gema', format: 'percent' },
  miningNoDepleteChance: { label: 'Veta Rica (no gasta)', format: 'percent' },
  miningSuccessChanceBonus: { label: 'Éxito de Minería', format: 'percent' },
  attackRangeWidth: { label: 'Ancho de Ataque', format: 'flat' },
  attackRangeLength: { label: 'Largo de Ataque', format: 'flat' },
  attackRangeBack: { label: 'Ataque hacia Atrás', format: 'flat' },
};

/** Nombre español (plural) de cada subtipo de arma, para labels de mods por arma. */
export const WEAPON_SUBTYPE_LABELS: Record<string, string> = {
  dagger: 'dagas',
  axe: 'hachas',
  mace: 'mazas',
  sword: 'espadas',
  staff: 'báculos',
  wand: 'varas',
  knuckles: 'nudilleras',
  bow: 'arcos',
  crossbow: 'ballestas',
  throwable: 'arrojadizas',
};

export interface ModifierDisplayRow {
  key: string;
  label: string;
  value: number;
  format: ModifierDisplayFormat;
}

/**
 * Convierte un `ItemModifiers` en filas legibles (omitiendo valores 0/undefined).
 * Las keys únicas se muestran como `flag` con su displayName.
 */
export function describeModifiers(mods: ItemModifiers | undefined): ModifierDisplayRow[] {
  if (!mods) return [];
  const rows: ModifierDisplayRow[] = [];
  for (const [key, raw] of Object.entries(mods)) {
    // Modifiers anidados por Reino: una fila por Reino con valor != 0.
    if ((key === 'damageVsKingdom' || key === 'magicFindVsKingdom') && raw && typeof raw === 'object') {
      const prefix = key === 'damageVsKingdom' ? 'Daño vs' : 'Rareza vs';
      for (const [kid, v] of Object.entries(raw as Record<string, number>)) {
        if (typeof v !== 'number' || v === 0) continue;
        rows.push({
          key: `${key}.${kid}`,
          label: `${prefix} ${getKingdomLabel(kid as KingdomId)}`,
          value: v,
          format: 'percent',
        });
      }
      continue;
    }
    // Modifiers anidados por Skill: una fila por skill con valor != 0.
    if ((key === 'skillXpBonus' || key === 'skillEffectiveness') && raw && typeof raw === 'object') {
      const suffix = key === 'skillXpBonus' ? 'XP' : 'Efectividad';
      for (const [sid, v] of Object.entries(raw as Record<string, number>)) {
        if (typeof v !== 'number' || v === 0) continue;
        const skillName = getSkillDef(sid)?.name ?? sid;
        rows.push({
          key: `${key}.${sid}`,
          label: `${skillName} · ${suffix}`,
          value: v,
          format: 'percent',
        });
      }
      continue;
    }
    // Daño +% anidado por subtipo de arma: una fila (percent) por arma con valor != 0.
    if (key === 'damageWithWeapon' && raw && typeof raw === 'object') {
      for (const [sub, v] of Object.entries(raw as Record<string, number>)) {
        if (typeof v !== 'number' || v === 0) continue;
        rows.push({
          key: `${key}.${sub}`,
          label: `Daño con ${WEAPON_SUBTYPE_LABELS[sub] ?? sub}`,
          value: v,
          format: 'percent',
        });
      }
      continue;
    }
    // Backstab crítico anidado por subtipo de arma: una fila (flag) por arma habilitada.
    if (key === 'backstabCrit' && raw && typeof raw === 'object') {
      for (const [sub, v] of Object.entries(raw as Record<string, number>)) {
        if (typeof v !== 'number' || v <= 0) continue;
        rows.push({
          key: `${key}.${sub}`,
          label: `Crítico por la espalda (${WEAPON_SUBTYPE_LABELS[sub] ?? sub})`,
          value: v,
          format: 'flag',
        });
      }
      continue;
    }
    const value = typeof raw === 'number' ? raw : 0;
    if (value === 0) continue;
    if (isUniqueModifierKey(key)) {
      const def = getUniqueModifier(key);
      rows.push({ key, label: def?.displayName ?? key, value, format: 'flag' });
      continue;
    }
    const labelDef = MODIFIER_LABELS[key as keyof ItemModifiers];
    rows.push({
      key,
      label: labelDef?.label ?? key,
      value,
      format: labelDef?.format ?? 'flat',
    });
  }
  return rows;
}

/**
 * Modifier (key) → nombre de ícono de stat (espejo de `STAT_ICON_MAP` de
 * `client/src/sprites/IconAtlases.ts`, mismos valores). Lo usa la WIKI para mostrar el
 * ícono de cada stat al lado del label (paridad con el panel de personaje in-game). Los
 * PNG viven en `wiki/docs/public/stat/<iconName>.png` (extraídos con gen_skill_icons.py).
 */
export const MODIFIER_ICON: Record<string, string> = {
  minDamage: 'physical_damage',
  maxDamage: 'physical_damage',
  physDef: 'defense',
  magicDef: 'defense',
  moveSpeed: 'movement_speed',
  attackSpeed: 'attack_speed',
  castSpeed: 'cast_time',
  critChance: 'critical',
  critMultiplier: 'critical',
  hp: 'hp',
  mana: 'mana',
  stamina: 'stamina',
  regenHP: 'hp_regen',
  regenMana: 'mana_regen',
  regenStamina: 'stamina',
  lifeStealPhysical: 'lifesteal',
  lifeStealMagic: 'lifesteal',
  reflectPhysicalDamage: 'reflect',
  reflectMagicDamage: 'reflect',
  healingOutput: 'healing',
  healingReceived: 'healing',
  magicFind: 'magic_find',
  evasion: 'evasion',
  fishingBonus: 'skill_fishing',
  spellDamagePercent: 'magic_damage',
  meleeDamagePercent: 'physical_damage',
  projectileDamagePercent: 'physical_damage',
  damagePercent: 'physical_damage',
};

/** Nombre de ícono de stat para una key de modifier (o null si no tiene). */
export function getModifierIcon(key: string): string | null {
  // Los sub-mods por Reino/Skill llevan sufijo `.<id>`; usar el prefijo base.
  const base = key.includes('.') ? key.slice(0, key.indexOf('.')) : key;
  return MODIFIER_ICON[base] ?? null;
}

/** Formatea el valor de una fila para mostrar (con signo). */
export function formatModifierValue(row: ModifierDisplayRow): string {
  if (row.format === 'flag') return 'sí';
  const sign = row.value >= 0 ? '+' : '';
  if (row.format === 'percent') return `${sign}${Math.round(row.value * 100)}%`;
  // flat: mostrar hasta 2 decimales, sin ceros colgando
  const rounded = Math.round(row.value * 100) / 100;
  return `${sign}${rounded}`;
}
